/**
 * The brief encouragement banner shown between questions.
 *
 * Two wording sets on one rhythm. Scorecards genuinely end in a gap report, so
 * their lines promise one. Event registration surveys (`assessment.type ===
 * 'registration'`) do not — they shape a session — so their lines must never
 * mention a gap, money, a report, or a fixed number of minutes. Surveys run from
 * 6 questions (FRWY, whose gate page promises "Six quick questions. Two
 * minutes.") to 17 (Summit), and "3 minutes to clarity" contradicted that page.
 *
 * ★ Switch on assessment type, never on `config.surveyMode`: that flag also
 * replaces the Result screen and suppresses the email sequence, which would kill
 * FRWY's Belt result. Pinned by taker/test/encouragement.test.ts.
 *
 * ★ The final question never gets a progress line. On a short survey the
 * halfway/third/three-quarter positions can land on the last question — a
 * three-question Step 2 said "Halfway there." on question 3 — so those lines
 * become `last` there. Banner positions are unchanged.
 *
 * Pure leaf — no imports — so Node can test it without a build step.
 */

interface WordingSet {
  first: string;
  second: string;
  nearlyDone: string;
  threeQuarters: string;
  half: string;
  third: string;
  last: string;
}

const SCORECARD: WordingSet = {
  first: "3 minutes to clarity. Let's find your gap.",
  second: 'Good — every answer sharpens your personalised analysis.',
  nearlyDone: 'One or two more — then we show you exactly where the money is.',
  threeQuarters: 'Almost there. Your personalised gap report is nearly ready.',
  half: "Halfway to knowing exactly what's holding your revenue back.",
  third: 'Your gap analysis is taking shape...',
  last: 'Last question — then we show you exactly where the money is.',
};

const EVENT: WordingSet = {
  first: 'Quick questions — they help shape the session around you.',
  second: 'Good — every answer helps us make the session about your firm.',
  nearlyDone: "One or two more and you're done.",
  threeQuarters: 'Almost there.',
  half: 'Halfway there.',
  third: 'Your picture is taking shape.',
  last: 'Last question.',
};

export function getEncouragement(idx: number, total: number, assessmentType?: string): string | null {
  const w = assessmentType === 'registration' ? EVENT : SCORECARD;
  const pct = (idx / total) * 100;
  if (idx === 0) return w.first;
  if (idx === 1) return w.second;
  if (pct >= 90) return w.nearlyDone;
  const isLast = idx === total - 1;
  if (pct >= 75 && idx === Math.ceil(total * 0.75)) return isLast ? w.last : w.threeQuarters;
  if (pct >= 50 && idx === Math.ceil(total * 0.5)) return isLast ? w.last : w.half;
  if (pct >= 33 && idx === Math.ceil(total * 0.33)) return isLast ? w.last : w.third;
  return null;
}
